/**
 * Razorpay Checkout helper — loads the official JS SDK on demand and
 * opens the Checkout modal with the options we need. Used by both the
 * customer checkout flow (tenant's Razorpay keys) AND the tenant plan
 * upgrade flow (platform Razorpay keys).
 *
 * Idempotent: re-calling `loadRazorpayCheckout()` returns the cached
 * promise once the script is in the DOM.
 */

const SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";
let _scriptPromise = null;

export function loadRazorpayCheckout() {
  if (_scriptPromise) return _scriptPromise;
  if (typeof window !== "undefined" && window.Razorpay) {
    _scriptPromise = Promise.resolve(window.Razorpay);
    return _scriptPromise;
  }
  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () => reject(new Error("Razorpay script failed to load")));
      return;
    }
    const tag = document.createElement("script");
    tag.src = SDK_URL;
    tag.async = true;
    tag.onload = () => resolve(window.Razorpay);
    tag.onerror = () => {
      _scriptPromise = null;
      reject(new Error("Razorpay script failed to load — check internet / ad blocker"));
    };
    document.head.appendChild(tag);
  });
  return _scriptPromise;
}

/**
 * Open Razorpay Checkout. Resolves to the success payload from
 * Razorpay (`{razorpay_order_id, razorpay_payment_id, razorpay_signature}`)
 * and REJECTS on user dismiss / failure so callers can `await`.
 *
 * @param {Object} opts
 * @param {string} opts.keyId             - Razorpay key_id (rzp_test_* or rzp_live_*)
 * @param {string} opts.orderId           - Razorpay order id (`order_xxx`)
 * @param {number} opts.amount            - Amount in paise (int)
 * @param {string} opts.currency          - e.g. "INR"
 * @param {string} opts.name              - Brand name shown in the modal
 * @param {string} [opts.description]
 * @param {string} [opts.image]           - Logo URL
 * @param {Object} [opts.prefill]         - { name, email, contact }
 * @param {Object} [opts.notes]           - String map
 * @param {string} [opts.themeColor]
 */
export async function openRazorpayCheckout(opts) {
  const RZP = await loadRazorpayCheckout();
  return new Promise((resolve, reject) => {
    const rzp = new RZP({
      key: opts.keyId,
      amount: opts.amount,
      currency: opts.currency || "INR",
      order_id: opts.orderId,
      name: opts.name || "",
      description: opts.description || "",
      image: opts.image || undefined,
      prefill: opts.prefill || {},
      notes: opts.notes || {},
      theme: { color: opts.themeColor || "#7A1C29" },
      handler: (resp) => resolve(resp),
      modal: {
        ondismiss: () => reject(new Error("dismissed")),
        escape: true,
      },
    });
    rzp.on("payment.failed", (resp) => {
      const desc = resp?.error?.description || "Payment failed";
      reject(new Error(desc));
    });
    rzp.open();
  });
}
